// @core/stores/task/TaskTaskCategoryStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { taskApi } from '@core/api';
import type {
  CreateTaskCategoryRequest,
  TaskCategory,
  UpdateTaskCategoryRequest,
} from '@core/types/task';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TaskTaskCategoryStore {
  // List state
  items: TaskCategory[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: TaskCategory | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, TaskCategory>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): TaskCategory | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: TaskCategory | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create task category
   */
  async CreateTaskCategory(data: CreateTaskCategoryRequest): Promise<TaskCategory> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.CreateTaskCategory(data);
      const item = response.category;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete task category
   */
  async DeleteTaskCategory(category_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteTaskCategory(category_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== category_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === category_id) {
          this.currentItem = null;
        }

        this.removeFromCache(category_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get task category
   */
  async GetTaskCategory(category_id: string): Promise<TaskCategory> {
    // Check cache first
    const cached = this.cache.get(category_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await taskApi.GetTaskCategory(category_id);
      const item = response.category;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List task categories
   */
  async ListTaskCategories(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListTaskCategories(params);

      runInAction(() => {
        this.items = response.categories?.categories ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update task category
   */
  async UpdateTaskCategory(category_id: string, data: UpdateTaskCategoryRequest): Promise<TaskCategory> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateTaskCategory(category_id, data);
      const item = response.category;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const taskTaskCategoryStore = new TaskTaskCategoryStore();
